import React, { useMemo } from 'react';
import { findCompany } from '@src/@data/utility/companies';
import { useCompanies } from '@src/@data/store/CompaniesProvider';
import { useCompany } from '@src/@data/store/CompanyProvider';
import { useMessages } from '@hooks/useMessages';
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledButtonDropdown,
} from 'reactstrap';
import { Link, useLocation } from 'react-router-dom';
import classNames from 'classnames';
import {
  getPath,
  ROUTE_CALENDARS,
  ROUTE_CAMPAIGNS,
  ROUTE_COMPANY,
  ROUTE_COMPANY_CLIENTS,
  ROUTE_DETAILED_STATISTICS,
  ROUTE_GIFT_CARDS,
  ROUTE_PRODUCTS,
  ROUTE_PROMO_CODES,
  ROUTE_RESERVATIONS,
  ROUTE_REVIEWS,
  ROUTE_SERVICE_GROUPS,
  ROUTE_SERVICES,
  ROUTE_TRANSACTIONS_STATISTICS,
  ROUTE_USER_ROLES,
  ROUTE_VENUES,
} from '@src/@data/router/consts';

const formatTitle = (title: string) => {
  if (title.length > 22) {
    return `${title.substring(0, 19)}...`;
  }
  return title;
};

const getRedirectPath = ({
  selectedCompanyId,
  pathname,
  newCompanyId,
}: {
  selectedCompanyId: string | undefined;
  pathname: string;
  newCompanyId: string;
}) => {
  if (selectedCompanyId && pathname.includes(selectedCompanyId)) {
    // redirect from page sections with an id other than company id
    if (pathname.includes('/products/')) {
      return getPath(ROUTE_PRODUCTS, { companyId: newCompanyId });
    }
    if (pathname.includes('/services/groups/')) {
      return getPath(ROUTE_SERVICE_GROUPS, { companyId: newCompanyId });
    }
    if (pathname.includes('/services/')) {
      return getPath(ROUTE_SERVICES, { companyId: newCompanyId });
    }
    if (pathname.includes('/statistics/detailed/')) {
      return getPath(ROUTE_DETAILED_STATISTICS, { companyId: newCompanyId });
    }
    if (pathname.includes('/statistics/transactions')) {
      return getPath(ROUTE_TRANSACTIONS_STATISTICS, {
        companyId: newCompanyId,
      });
    }
    if (pathname.includes('/calendars/')) {
      return getPath(ROUTE_CALENDARS, { companyId: newCompanyId });
    }
    if (pathname.includes('/promo/')) {
      return getPath(ROUTE_PROMO_CODES, { companyId: newCompanyId });
    }
    if (pathname.includes('/clients/')) {
      return getPath(ROUTE_COMPANY_CLIENTS, { companyId: newCompanyId });
    }
    if (
      pathname.includes('/reservations/time/') ||
      pathname.includes('/reservations/slot/')
    ) {
      return getPath(ROUTE_RESERVATIONS, { companyId: newCompanyId });
    }
    if (pathname.includes('/venues/')) {
      return getPath(ROUTE_VENUES, { companyId: newCompanyId });
    }
    if (pathname.includes('/user-roles/')) {
      return getPath(ROUTE_USER_ROLES, { companyId: newCompanyId });
    }
    if (pathname.includes('/reviews/')) {
      return getPath(ROUTE_REVIEWS, { companyId: newCompanyId });
    }
    if (pathname.includes('/user-roles/')) {
      return getPath(ROUTE_USER_ROLES, { companyId: newCompanyId });
    }
    if (pathname.includes('/campaigns/')) {
      return getPath(ROUTE_CAMPAIGNS, { companyId: newCompanyId });
    }
    if (pathname.includes('/gift-cards/')) {
      return getPath(ROUTE_GIFT_CARDS, { companyId: newCompanyId });
    }
    return pathname.replace(selectedCompanyId, newCompanyId);
  }
  return getPath(ROUTE_COMPANY, { companyId: newCompanyId });
};

const MenuCompaniesHeader: React.FC = () => {
  const { messages } = useMessages();
  const { pathname } = useLocation();

  const { companies } = useCompanies();
  const { selectedCompanyId } = useCompany();

  const companySelectorText = useMemo(() => {
    if (!companies || companies.length === 0) {
      return messages('nav_select_company_no_companies');
    }
    if (!selectedCompanyId) {
      return messages('nav_select_company');
    }
    const selectedCompany = findCompany(companies, selectedCompanyId);
    return !selectedCompany
      ? selectedCompanyId
      : formatTitle(selectedCompany.title);
  }, [companies, messages, selectedCompanyId]);

  const showDropDown = useMemo(() => {
    const noCompanies = !companies || companies.length === 0;
    const onlyCompanySelected =
      !!companies &&
      companies.length === 1 &&
      companies[0].id === selectedCompanyId;
    return !noCompanies && !onlyCompanySelected;
  }, [companies, selectedCompanyId]);

  return (
    <UncontrolledButtonDropdown className="w-100">
      <DropdownToggle
        outline
        color="primary"
        className={classNames(
          'w-100',
          showDropDown ? 'cursor-pointer' : 'cursor-default'
        )}
        caret={showDropDown}
      >
        {companySelectorText}
      </DropdownToggle>
      {showDropDown && !!companies && (
        <DropdownMenu className="overflow-y-scroll max-height-500 w-100">
          {companies.map((company) => {
            return (
              <DropdownItem
                key={company.id}
                tag={Link}
                active={company.id === selectedCompanyId}
                className="text-wrap"
                to={getRedirectPath({
                  selectedCompanyId,
                  pathname,
                  newCompanyId: company.id,
                })}
              >
                {company.title}
              </DropdownItem>
            );
          })}
        </DropdownMenu>
      )}
    </UncontrolledButtonDropdown>
  );
};

export default MenuCompaniesHeader;
